import { inject, Injectable, InjectionToken, Provider } from '@angular/core';
import { ENVIRONMENT } from '../../../environments/environment.interface';
import { PaginatedResponse } from '../../shared/pagination/paginated-response';
import { ApiSponsor, ApiSponsorExtended, ApiSponsorPerson, CreateSponsorPersonApiBody } from '../api-types/sponsor.api';
import {
	CreateSponsorPerson,
	Sponsor,
	SponsorExtended,
	SponsorPerson,
	UpdateSponsorPerson,
} from '../app-types/sponsor.app';
import {
	transformCreateSponsorPersonToApiBody,
	transformApiSponsorToAppSponsorExtended,
	transformApiSponsorPersonsToAppSponsorPersons,
	transformApiSponsorsToAppSponsors,
	transformApiSponsorPersonNeighborsToAppSponsorPersonNeighbors,
} from '../transformers/sponsor.transform';
import { BaseRequestService } from './base-request.service';
import { buildPaginationQuery } from '../../shared/pagination/pagination.builder';
import { environment } from '../../../environments/environment';
import { NeighborContext } from '../app-types/neighbors.app';
import { ApiNeighborContext } from '../api-types/neighbor.api';

export const SPONSOR_SERVICE = new InjectionToken<SponsorProvider>('SPONSOR_SERVICE');

export interface SponsorProvider extends BaseRequestService {
	getAllByIntegrationOfficeId(
		integrationOfficeId: number,
		offset?: number,
		range?: number,
	): Promise<PaginatedResponse<Sponsor>>;
	getAllSponsors(offset?: number, range?: number): Promise<PaginatedResponse<Sponsor>>;
	getAllSponsorsForTypeOfCare(
		typeOfCareId: string | number,
		offset?: number,
		range?: number,
	): Promise<PaginatedResponse<Sponsor>>;
	getById(id: number | string): Promise<SponsorExtended>;
	getPersons(id: number | string, offset?: number, range?: number): Promise<PaginatedResponse<SponsorPerson>>;
	getPersonById(sponsorId: number | string, personId: number | string): Promise<SponsorPerson>;
	createPerson(req: CreateSponsorPerson): Promise<{ id: string }>;
	updatePerson(req: UpdateSponsorPerson): Promise<{ id: string }>;
	deactivatePerson(sponsorId: number | string, personId: number | string): Promise<void>;
	getPersonNeighbors(sponsorId: number | string, personId: number | string): Promise<NeighborContext<SponsorPerson>>;
}

/**
 * Fetches everything related to sponsors from the API.
 */
@Injectable()
export class SponsorService extends BaseRequestService implements SponsorProvider {
	private readonly defaultOffset = environment.pagination.offset;
	private readonly defaultRange = environment.pagination.range;
	private readonly defaultPageSize = environment.defaultPageSize;

	public async getAllByIntegrationOfficeId(integrationOfficeId: number, offset?: number, range?: number) {
		const response = await this.get<PaginatedResponse<ApiSponsor>>(
			`sponsors/byIntegrationOffice/${integrationOfficeId}`,
			{
				offset: offset ?? this.defaultOffset,
				range: range ?? this.defaultRange,
			},
		);
		const sponsors = transformApiSponsorsToAppSponsors(response.entities);
		const page: PaginatedResponse<Sponsor> = {
			entities: sponsors,
			offset: response.offset,
			range: response.range,
			totalEntities: response.totalEntities,
		};
		return page;
	}

	public async getAllSponsors(offset?: number, range?: number) {
		const response = await this.get<PaginatedResponse<ApiSponsor>>(
			'sponsors',
			buildPaginationQuery(offset ?? this.defaultOffset, range ?? this.defaultRange),
		);
		const sponsors = transformApiSponsorsToAppSponsors(response.entities);
		const page: PaginatedResponse<Sponsor> = {
			entities: sponsors,
			offset: response.offset,
			range: response.range,
			totalEntities: response.totalEntities,
		};
		return page;
	}

	public async getAllSponsorsForTypeOfCare(typeOfCareId: string | number, offset?: number, range?: number) {
		const response = await this.get<PaginatedResponse<ApiSponsor>>(`sponsors/typeOfCares/${typeOfCareId}`, {
			offset: offset ?? this.defaultOffset,
			range: range ?? this.defaultRange,
		});
		const sponsors = transformApiSponsorsToAppSponsors(response.entities);
		const page: PaginatedResponse<Sponsor> = {
			entities: sponsors,
			offset: response.offset,
			range: response.range,
			totalEntities: response.totalEntities,
		};
		return page;
	}

	public async getById(id: number | string): Promise<SponsorExtended> {
		const response = await this.get<ApiSponsorExtended>(`sponsors/${id}`);
		return transformApiSponsorToAppSponsorExtended(response);
	}

	public async getPersons(id: number | string, offset = 0, range = this.defaultPageSize) {
		try {
			const response = await this.get<PaginatedResponse<ApiSponsorPerson>>(
				`sponsors/${id}/sponsorPersons`,
				buildPaginationQuery(offset, range),
			);
			return {
				entities: transformApiSponsorPersonsToAppSponsorPersons(response.entities ?? []),
				offset: response.offset,
				range: response.range,
				totalEntities: response.totalEntities,
			};
		} catch {
			return {
				entities: [],
				offset,
				range,
				totalEntities: 0,
			};
		}
	}

	public async createPerson(req: CreateSponsorPerson) {
		const body = transformCreateSponsorPersonToApiBody(req);
		return this.post<{ id: string }, CreateSponsorPersonApiBody>('sponsorPerson', body);
	}

	public async updatePerson(req: UpdateSponsorPerson) {
		const body = transformCreateSponsorPersonToApiBody(req);
		return this.put<{ id: string }, CreateSponsorPersonApiBody>(
			`sponsors/${req.sponsorId}/sponsorPersons/${req.id}`,
			body,
		);
	}

	public async getPersonById(sponsorId: number | string, personId: number | string): Promise<SponsorPerson> {
		try {
			const response = await this.get<ApiSponsorPerson>(`sponsors/${sponsorId}/sponsorPersons/${personId}`);
			return transformApiSponsorPersonsToAppSponsorPersons([response])[0];
		} catch {
			const page = await this.getPersons(sponsorId, 0, this.defaultPageSize);
			const person = page.entities.find((p) => p.id === Number(personId));
			if (!person) throw new Error(`Sponsor person ${personId} not found for sponsor ${sponsorId}`);
			return person;
		}
	}

	public async deactivatePerson(sponsorId: number | string, personId: number | string): Promise<void> {
		await this.remove<void>(`sponsors/${sponsorId}/sponsorPersons/${personId}`);
	}

	public async getPersonNeighbors(
		sponsorId: number | string,
		personId: number | string,
	): Promise<NeighborContext<SponsorPerson>> {
		const response = await this.get<ApiNeighborContext<ApiSponsorPerson>>(
			`sponsors/${sponsorId}/sponsorPersons/${personId}/neighbors`,
		);
		return transformApiSponsorPersonNeighborsToAppSponsorPersonNeighbors(response);
	}
}

@Injectable()
export class SponsorMockService extends BaseRequestService implements SponsorProvider {
	private readonly env = inject(ENVIRONMENT);
	public constructor() {
		super();
		console.warn('⛔️⛔️⛔️ Sponsor Mock Service: You are currently using a mock service!');
	}

	private sponsors: Sponsor[] = [
		{
			id: 1,
			integrationOfficeId: 1,
			name: 'T-INA Tupfenland',
		},
		{
			id: 2,
			integrationOfficeId: 1,
			name: 'kein P2 Auftraggeber',
		},
	];

	public getAllSponsors = async (offset?: number, range?: number) => {
		return {
			entities: this.sponsors,
			offset: offset ?? 0,
			range: range ?? this.sponsors.length,
			totalEntities: this.sponsors.length,
		};
	};

	public getAllByIntegrationOfficeId = async (_integrationOfficeId: number) => {
		return this.getAllSponsors();
	};

	public getAllSponsorsForTypeOfCare = async (_typeOfCareId: string | number) => {
		return this.getAllSponsors();
	};

	public getById = async (id: number | string): Promise<SponsorExtended> => {
		const sponsor = this.sponsors.find((x) => x.id === Number(id));
		if (!sponsor) {
			throw new Error(`Sponsor with id ${id} not found`);
		}
		return {
			...sponsor,
			contactPoints: [],
		};
	};

	public getPersons = async (_id: number | string, offset = 0, range = this.env.defaultPageSize) => {
		return {
			entities: [],
			offset,
			range,
			totalEntities: 0,
		};
	};

	public getPersonById = async (_sponsorId: number | string, personId: number | string): Promise<SponsorPerson> => {
		return {
			id: Number(personId),
			name: '',
			firstName: '',
			contactPoints: [],
		};
	};

	public createPerson = async (_req: CreateSponsorPerson): Promise<{ id: string }> => {
		return { id: '1' };
	};

	public updatePerson = async (_req: UpdateSponsorPerson): Promise<{ id: string }> => {
		return { id: '1' };
	};

	public deactivatePerson = async (_sponsorId: number | string, _personId: number | string): Promise<void> => {
		return;
	};

	public getPersonNeighbors = async (
		_sponsorId: number | string,
		personId: number | string,
	): Promise<NeighborContext<SponsorPerson>> => {
		const id = Number(personId);
		const createMockPerson = (id: number, firstName: string, name: string): SponsorPerson => ({
			id,
			firstName,
			name,
			title: '',
			role: '',
			remark: '',
			hasBeenClarified: false,
			address: undefined,
			contactPoints: [],
			gender: undefined,
			personType: undefined,
			isDeactivated: false,
		});

		return {
			current: createMockPerson(id, 'Current', `Person ${id}`),
			previous: id > 1 ? createMockPerson(id - 1, 'Previous', `Person ${id - 1}`) : null,
			next: createMockPerson(id + 1, 'Next', `Person ${id + 1}`),
		};
	};
}

export const provideSponsorApi: Provider = {
	provide: SPONSOR_SERVICE,
	useClass: SponsorService,
};
